import React from "react";
import { Marker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import { bestWildcadMatch, sameFire, matchPerimeter } from "../../lib/dedupe";
import FireNameLabel from "./FireNameLabel";
import { flameIcon, flameSize } from "./IncidentLayer";

// NIFC-flavored marker palette
const PALETTE = {
  WF: { fill: "#FF5630", label: "Wildfire" },
  RX: { fill: "#9F7AEA", label: "Prescribed" },
  CX: { fill: "#FF8C42", label: "Complex" },
  default: { fill: "#A0AEC0", label: "Incident" },
};

function palette(typeCategory) {
  return PALETTE[typeCategory] || PALETTE.default;
}

// Rendered as divIcon markers (not CircleMarkers) so the red bubbles sit in
// the marker pane ABOVE DNR flame / camera icons and stay clickable.
const _bubbleCache = {};
function bubbleIcon(fill, active, hovered) {
  const key = `${fill}-${active}-${hovered}`;
  if (!_bubbleCache[key]) {
    const base = active ? 16 : 12;
    const size = hovered ? Math.round(base * 1.75) : base;
    _bubbleCache[key] = L.divIcon({
      className: "flame-marker",
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 16 16" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.5)); width:${size}px; height:${size}px;">
        <circle cx="8" cy="8" r="${active ? 6.6 : 5.4}" fill="${fill}" fill-opacity="${active ? 0.95 : 0.55}" stroke="#0A0D0C" stroke-width="1.5" ${active ? "" : 'stroke-dasharray="2 2"'}/>
      </svg>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  }
  return _bubbleCache[key];
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function NifcIncidentLayer({ incidents, activeId = null, onMarkerHover, wildcadIncidents = [], linkIncidents = null, dnrFires = [], perimeters = [] }) {
  const linkList = linkIncidents || wildcadIncidents;
  return incidents.map((n) => {
    if (n.latitude == null || n.longitude == null) return null;
    const meta = palette(n.type_category);
    const active = n.is_active;
    const hovered = n.id === activeId;
    // Rendered-flame match (label dedupe) vs any-record match (detail link)
    const wcRendered = bestWildcadMatch(wildcadIncidents, n);
    const wcLink = bestWildcadMatch(linkList, n);
    // Skip the name label when a DNR flame or WildCAD flame already labels
    // this fire — avoids side-by-side duplicate chips.
    const dnrDup = dnrFires.some((d) => sameFire(d, n));
    const pm =
      perimeters.find((p) => p.irwin_id && n.irwin_id && p.irwin_id === n.irwin_id) ||
      matchPerimeter(n, perimeters);
    const mappedAcres =
      pm?.acres != null &&
      (n.incident_size == null || Math.round(pm.acres) !== Math.round(n.incident_size))
        ? pm.acres
        : null;
    // Active NIFC fires render as red flames (acreage-scaled) so major fires
    // like out-of-county perimeter fires are unmissable; inactive keep bubbles.
    const base = flameSize(n.incident_size);
    const icon = active
      ? flameIcon(meta.fill, hovered ? Math.round(base * 1.75) : base, "")
      : bubbleIcon(meta.fill, active, hovered);
    return (
      <Marker
        key={`nifc-${n.id}`}
        pane="fire-icons"
        position={[n.latitude, n.longitude]}
        icon={icon}
        zIndexOffset={hovered ? 1200 : 900}
        eventHandlers={{
          mouseover: () => onMarkerHover && onMarkerHover(n.id),
          mouseout: () => onMarkerHover && onMarkerHover(null),
          click: () => onMarkerHover && onMarkerHover(null),
        }}
      >
        <Popup>
          <div className="min-w-[220px]" data-testid={`nifc-popup-${n.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: meta.fill }}>
              NIFC · {meta.label}
            </div>
            <div className="font-display font-bold text-sm text-tac-text mb-1 leading-snug">
              {n.name}
            </div>
            <div className="text-[14px] text-tac-dim font-mono-tac tabular space-y-0.5">
              {n.incident_size != null && (
                <div>
                  {Math.round(n.incident_size).toLocaleString()} ac
                  {mappedAcres != null ? " official" : ""}
                </div>
              )}
              {mappedAcres != null && (
                <div>{Math.round(mappedAcres).toLocaleString()} ac mapped perimeter</div>
              )}
              {n.percent_contained != null && (
                <div>{Math.round(n.percent_contained)}% contained</div>
              )}
              {n.county && <div>{n.county} County, WA</div>}
              {n.protecting_agency && <div>Protecting: {n.protecting_agency}</div>}
              {n.discovered_at && <div>Discovered {fmtDate(n.discovered_at)}</div>}
              {n.modified_at && <div>Updated {fmtDate(n.modified_at)}</div>}
            </div>
            {n.short_description && (
              <div className="text-[11px] text-tac-text mt-2 leading-snug">
                {n.short_description}
              </div>
            )}
            {wcLink && (
              <Link
                to={`/incident/${wcLink.id}`}
                data-testid={`nifc-popup-details-${n.id}`}
                className="inline-block mt-2 text-[11px] font-bold uppercase tracking-wider hover:underline"
                style={{ color: meta.fill }}
              >
                View details →
              </Link>
            )}
            <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
              Source: NIFC WFIGS · {n.unique_fire_id || n.irwin_id?.slice(1, 9)}
            </div>
          </div>
        </Popup>
        {!wcRendered && !dnrDup && <FireNameLabel name={n.name} acres={n.incident_size} />}
      </Marker>
    );
  });
}
